import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useDropdownAnchor } from './useDropdownAnchor';
import { FLOW_PORTAL_PROPS } from '../useClickOutside';
import type { FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import type { SIGNAL_COLUMN_FILTER_ALIASES } from '@shared/flow/signal-view';
import { fieldExamples } from '@shared/flow/field-catalog-order';
import { matchesFieldQuery, sectionFieldOptions } from '../table-core/field-sections';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * Searchable, sectioned field select for a filter condition's field row
 * (plan U4, prototype `fieldPop()` ~line 691). Matches on the field's LABEL
 * OR its `group` (object) name — typing "account" surfaces every `Account:`
 * field even though most of their labels don't contain the word "account".
 *
 * Portaled to `document.body` (via `useAnchoredPosition`) so the panel
 * escapes the filter popover's own scroll box and can flip/clamp to the
 * viewport independently of it — same pattern as `ContactActionMenus.tsx`'s
 * dropdowns.
 */
export type FieldPickerAlias = Omit<(typeof SIGNAL_COLUMN_FILTER_ALIASES)[number], 'columnKey'>;

export interface FieldPickerProps {
  fields: FilterFieldCatalogEntry[];
  /** The currently-selected field key (may be stale/unknown — renders as a plain key). */
  value: string;
  onSelect: (key: string) => void;
  /** Non-filterable columns that point at the field(s) to filter with instead. Rendered as inert hint rows. */
  aliases?: ReadonlyArray<FieldPickerAlias>;
}

export function FieldPicker({ fields, value, onSelect, aliases }: FieldPickerProps) {
  const { open, setOpen, triggerRef, menuRef, menuStyle } = useDropdownAnchor();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = fields.find(f => f.key === value);

  // Each field's example line (hand-written hint, or derived from a closed values list) — shown under the label (display only, not searched).
  const hinted = useMemo(() => fields.map(f => ({ field: f, hint: fieldExamples(f) })), [fields]);
  const matches = useMemo(
    () => hinted.filter(h => matchesFieldQuery({ label: h.field.label, group: h.field.group }, query)).map(h => ({ ...h.field, hint: h.hint })),
    [hinted, query],
  );

  // Catalogs are already grouped contiguously (`groupContiguous`, see
  // filter-fields.ts) — fold consecutive same-group entries into sections
  // without re-sorting, so catalog order (and section order) is preserved.
  // Shared with the Glaze Group/Sort picker (`table-core/field-sections.ts`).
  const sections = useMemo(() => {
    const base = sectionFieldOptions(matches).map(s => ({ group: s.group ?? '', entries: s.entries, aliasRows: [] as string[] }));
    if (!aliases?.length) return base;
    const byKey = new Map(fields.map(f => [f.key, f]));
    const groupOrder = [...new Set(fields.map(f => f.group ?? ''))];
    for (const alias of aliases) {
      if (!matchesFieldQuery({ label: alias.label }, query)) continue;
      const present = alias.targets.map(t => byKey.get(t)).filter((f): f is FilterFieldCatalogEntry => !!f);
      if (present.length === 0) continue;
      const group = present[0].group ?? '';
      let section = base.find(s => s.group === group);
      if (!section) {
        section = { group, entries: [], aliasRows: [] };
        const at = base.findIndex(s => groupOrder.indexOf(s.group) > groupOrder.indexOf(group));
        base.splice(at === -1 ? base.length : at, 0, section);
      }
      section.aliasRows.push(`${alias.label} — filter with ${present.map(f => f.label).join(', ')}`);
    }
    return base;
  }, [matches, aliases, fields, query]);

  const openPicker = () => {
    setOpen(o => !o);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={openPicker}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1 w-full px-2 py-1 text-[11.5px] text-left border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
      >
        <span className="truncate flex-1">
          {selected ? (
            <>
              <span className="text-content-tertiary dark:text-nightshade-500">{selected.group}: </span>
              {selected.label}
            </>
          ) : (
            'Select field…'
          )}
        </span>
        <ChevronDown className="w-3 h-3 shrink-0 text-content-tertiary dark:text-nightshade-500" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="field-picker-menu"
          {...FLOW_PORTAL_PROPS}
          style={menuStyle}
          className="w-[28rem] rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 shadow-lg overflow-hidden"
        >
          <div className="p-1.5 border-b border-secondary dark:border-nightshade-700">
            <input
              {...AUTOFILL_IGNORE_PROPS}
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search fields…"
              className="w-full px-2 py-1 text-[12px] border border-secondary dark:border-nightshade-700 rounded bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder outline-none"
            />
          </div>
          <div className="max-h-[60dvh] overflow-auto py-1">
            {sections.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-content-tertiary dark:text-nightshade-500">No fields match</div>
            )}
            {sections.map(section => (
              <div key={section.group}>
                <div className="px-2.5 py-1 text-[10px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500">
                  {section.group}
                </div>
                {section.entries.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    aria-label={f.label}
                    aria-description={f.hint}
                    onClick={() => { onSelect(f.key); setOpen(false); setQuery(''); }}
                    className={`block w-full text-left px-2.5 py-1 text-[12px] ${
                      f.key === value
                        ? 'bg-bg-blue-light text-content-action dark:text-blueberry-400 font-medium'
                        : 'text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
                    }`}
                  >
                    {f.label}
                    {f.hint && (
                      <span data-testid="field-hint" className="block truncate text-[11px] font-normal text-content-tertiary dark:text-nightshade-500">
                        {f.hint}
                      </span>
                    )}
                  </button>
                ))}
                {section.aliasRows.map(text => (
                  <div key={text} aria-disabled="true" className="px-2.5 py-1 text-[12px] text-content-tertiary dark:text-nightshade-500">
                    {text}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
